/**
 * Review Service
 * Handles property, landlord, and tenant reviews
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { getFirebaseDB } from "@/lib/firebase";
import { Review, ReviewStatus } from "@/types/types";

/**
 * Create a new review
 */
export async function createReview(
  reviewData: Omit<Review, "id" | "createdAt" | "updatedAt" | "helpfulCount" | "reportCount">
): Promise<string> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  const newReviewRef = doc(reviewsRef);

  const review: Review = {
    ...reviewData,
    id: newReviewRef.id,
    helpfulCount: 0,
    reportCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(newReviewRef, {
    ...review,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Update aggregate rating
  if (reviewData.propertyId) {
    await updatePropertyRating(reviewData.propertyId);
  }

  return newReviewRef.id;
}

/**
 * Get review by ID
 */
export async function getReviewById(reviewId: string): Promise<Review | null> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewRef = doc(db, "reviews", reviewId);
  const reviewSnap = await getDoc(reviewRef);

  if (!reviewSnap.exists()) {
    return null;
  }

  const data = reviewSnap.data();
  return {
    ...data,
    id: reviewSnap.id,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
    moderatedAt: data.moderatedAt?.toDate?.()?.toISOString() || data.moderatedAt,
  } as Review;
}

/**
 * Get reviews for a property
 */
export async function getPropertyReviews(
  propertyId: string,
  limitCount: number = 10,
  onlyApproved: boolean = true
): Promise<Review[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  let q = query(
    reviewsRef,
    where("propertyId", "==", propertyId),
    where("type", "==", "property")
  );

  if (onlyApproved) {
    q = query(q, where("status", "==", "approved"));
  }

  q = query(q, orderBy("createdAt", "desc"), limit(limitCount));

  const querySnapshot = await getDocs(q);
  const reviews: Review[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    reviews.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      moderatedAt: data.moderatedAt?.toDate?.()?.toISOString() || data.moderatedAt,
    } as Review);
  });

  return reviews;
}

/**
 * Get reviews by a specific reviewer
 */
export async function getReviewerReviews(reviewerId: string): Promise<Review[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  const q = query(
    reviewsRef,
    where("reviewerId", "==", reviewerId),
    orderBy("createdAt", "desc")
  );

  const querySnapshot = await getDocs(q);
  const reviews: Review[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    reviews.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      moderatedAt: data.moderatedAt?.toDate?.()?.toISOString() || data.moderatedAt,
    } as Review);
  });

  return reviews;
}

/**
 * Get reviews for a landlord
 */
export async function getLandlordReviews(
  landlordId: string,
  limitCount: number = 10
): Promise<Review[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  const q = query(
    reviewsRef,
    where("landlordId", "==", landlordId),
    where("type", "==", "landlord"),
    where("status", "==", "approved"),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );

  const querySnapshot = await getDocs(q);
  const reviews: Review[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    reviews.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      moderatedAt: data.moderatedAt?.toDate?.()?.toISOString() || data.moderatedAt,
    } as Review);
  });

  return reviews;
}

/**
 * Get pending reviews for moderation
 */
export async function getPendingReviews(limitCount: number = 50): Promise<Review[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  const q = query(
    reviewsRef,
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
    limit(limitCount)
  );

  const querySnapshot = await getDocs(q);
  const reviews: Review[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    reviews.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      moderatedAt: data.moderatedAt?.toDate?.()?.toISOString() || data.moderatedAt,
    } as Review);
  });

  return reviews;
}

/**
 * Update review status (moderate)
 */
export async function moderateReview(
  reviewId: string,
  status: ReviewStatus,
  moderatedBy: string,
  moderationNotes?: string
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewRef = doc(db, "reviews", reviewId);

  await updateDoc(reviewRef, {
    status,
    moderatedBy,
    moderatedAt: serverTimestamp(),
    moderationNotes: moderationNotes || "",
    updatedAt: serverTimestamp(),
  });

  // Update property rating if approved
  if (status === "approved") {
    const review = await getReviewById(reviewId);
    if (review?.propertyId) {
      await updatePropertyRating(review.propertyId);
    }
  }

  return true;
}

/**
 * Mark review as helpful
 */
export async function markReviewHelpful(reviewId: string): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewRef = doc(db, "reviews", reviewId);
  await updateDoc(reviewRef, {
    helpfulCount: increment(1),
  });

  return true;
}

/**
 * Report review
 */
export async function reportReview(reviewId: string): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewRef = doc(db, "reviews", reviewId);
  await updateDoc(reviewRef, {
    reportCount: increment(1),
    status: "flagged",
  });

  return true;
}

/**
 * Delete review
 */
export async function deleteReview(reviewId: string): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  // Get review data before deleting to update property rating
  const review = await getReviewById(reviewId);
  
  const reviewRef = doc(db, "reviews", reviewId);
  await deleteDoc(reviewRef);

  // Update property rating after deletion
  if (review?.propertyId) {
    await updatePropertyRating(review.propertyId);
  }

  return true;
}

/**
 * Check if user can review a property
 */
export async function canReviewProperty(
  userId: string,
  propertyId: string
): Promise<{ canReview: boolean; reason?: string }> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  // Check if user has already reviewed this property
  const reviewsRef = collection(db, "reviews");
  const existingReviewQuery = query(
    reviewsRef,
    where("reviewerId", "==", userId),
    where("propertyId", "==", propertyId),
    where("type", "==", "property")
  );

  const existingReviews = await getDocs(existingReviewQuery);
  if (!existingReviews.empty) {
    return {
      canReview: false,
      reason: "You have already reviewed this property",
    };
  }

  // TODO: Check if user has completed lease at this property
  // For now, allow all verified users to review

  return { canReview: true };
}

/**
 * Update property's aggregate rating
 */
async function updatePropertyRating(propertyId: string): Promise<void> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  // Get all approved reviews for this property
  const reviewsRef = collection(db, "reviews");
  const q = query(
    reviewsRef,
    where("propertyId", "==", propertyId),
    where("type", "==", "property"),
    where("status", "==", "approved")
  );

  const querySnapshot = await getDocs(q);
  
  let totalRating = 0;
  let count = 0;

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    totalRating += data.rating || 0;
    count++;
  });

  const averageRating = count > 0 ? totalRating / count : 0;

  // Update property document
  const propertyRef = doc(db, "properties", propertyId);
  await updateDoc(propertyRef, {
    averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
    reviewCount: count,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get property rating summary
 */
export async function getPropertyRatingSummary(propertyId: string): Promise<{
  averageRating: number;
  totalReviews: number;
  ratingDistribution: { [key: number]: number };
}> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const reviewsRef = collection(db, "reviews");
  const q = query(
    reviewsRef,
    where("propertyId", "==", propertyId),
    where("type", "==", "property"),
    where("status", "==", "approved")
  );

  const querySnapshot = await getDocs(q);
  
  const ratingDistribution: { [key: number]: number } = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  };

  let totalRating = 0;
  let count = 0;

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    const rating = data.rating || 0;
    totalRating += rating;
    count++;
    ratingDistribution[rating] = (ratingDistribution[rating] || 0) + 1;
  });

  const averageRating = count > 0 ? totalRating / count : 0;

  return {
    averageRating: Math.round(averageRating * 10) / 10,
    totalReviews: count,
    ratingDistribution,
  };
}
